import { useState, useRef, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { 
  User, Mail, MapPin, Shield, CheckCircle, Upload, 
  Save, ArrowLeft, Star, Calendar, Edit2, X,
  Wallet, Clock, TrendingUp, Package, ExternalLink, Loader2
} from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import { getImageUrl } from "@/lib/imageUtils";
import api from "@/lib/api";


function StripeConnectPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchStatus = async () => {
    try {
      const res = await api.get("/user/stripe-connect/status");
      setStatus(res.data);
    } catch (err) {
      // If the endpoint fails, fall back to neutral placeholder
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Handle return-from-Stripe redirect params
  useEffect(() => {
    const onboarding = searchParams.get("onboarding");
    if (!onboarding) return;

    if (onboarding === "complete") {
      toast.success("Welcome back! Refreshing your Stripe status...");
      fetchStatus();
      // Clean URL
      searchParams.delete("onboarding");
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    } else if (onboarding === "refresh") {
      // The previous Account Link expired — auto-regenerate
      (async () => {
        try {
          const res = await api.post("/user/stripe-connect/account-link");
          if (res.data?.url) window.location.href = res.data.url;
        } catch (err) {
          toast.error("Could not regenerate Stripe onboarding link. Please try again.");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const startOnboarding = async () => {
    setRedirecting(true);
    try {
      const res = await api.post("/user/stripe-connect/create-account");
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start Stripe onboarding.");
        setRedirecting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Stripe onboarding.");
      setRedirecting(false);
    }
  };

  const resumeOnboarding = async () => {
    setRedirecting(true);
    try {
      const res = await api.post("/user/stripe-connect/account-link");
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not resume Stripe onboarding.");
        setRedirecting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resume Stripe onboarding.");
      setRedirecting(false);
    }
  };

  const openDashboard = async () => {
    setRedirecting(true);
    try {
      const res = await api.post("/user/stripe-connect/dashboard-link");
      if (res.data?.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open Stripe Dashboard.");
    } finally {
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 text-center" data-testid="stripe-connect-loading">
        <Loader2 className="w-6 h-6 mx-auto text-stone-400 animate-spin mb-3" />
        <p className="text-sm text-stone-500">Loading payout status…</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 text-center" data-testid="stripe-connect-placeholder">
        <Wallet className="w-7 h-7 text-stone-400 mx-auto mb-3" />
        <p className="text-sm text-stone-500">Payouts unavailable right now. Please try again later.</p>
      </div>
    );
  }

  // State: Caribbean lender
  if (status.requires_manual_processing) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 sm:p-10 text-center" data-testid="caribbean-payouts-card">
        <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
          <Wallet className="w-7 h-7 text-amber-600" />
        </div>
        <h3 className="font-heading font-semibold text-xl text-stone-900 mb-3" data-testid="caribbean-payouts-heading">
          Caribbean payouts coming soon
        </h3>
        <p className="text-sm text-stone-600 leading-relaxed max-w-lg mx-auto" data-testid="caribbean-payouts-body">
          We're finalizing our payout integration for the Caribbean. You can list items and accept bookings now — our team will reach out to you directly to arrange payment for your first booking.
          {" "}Questions? Email <a href="mailto:info@paradiseborrowed.com" className="text-coral underline">info@paradiseborrowed.com</a>.
        </p>
      </div>
    );
  }

  // State 1: No account yet
  if (!status.has_account) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 sm:p-10 text-center" data-testid="stripe-connect-state-1">
        <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
          <Wallet className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="font-heading font-semibold text-xl text-stone-900 mb-3" data-testid="stripe-connect-heading-1">
          Set up payouts with Stripe
        </h3>
        <p className="text-sm text-stone-600 leading-relaxed max-w-lg mx-auto mb-6">
          Connect a Stripe Express account so we can send your earnings directly to your bank. Onboarding is hosted securely by Stripe and usually takes 2–3 minutes.
        </p>
        <Button
          onClick={startOnboarding}
          disabled={redirecting}
          className="rounded-full bg-ink hover:bg-stone-800 text-white px-6 h-11 gap-2"
          data-testid="stripe-connect-setup-btn"
        >
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          {redirecting ? "Redirecting…" : "Set up payouts with Stripe"}
        </Button>
      </div>
    );
  }

  // State 2: Onboarding incomplete
  if (status.has_account && !status.onboarding_complete) {
    return (
      <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-8 sm:p-10 text-center" data-testid="stripe-connect-state-2">
        <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
          <Clock className="w-7 h-7 text-amber-600" />
        </div>
        <h3 className="font-heading font-semibold text-xl text-stone-900 mb-3" data-testid="stripe-connect-heading-2">
          Finish payout setup
        </h3>
        <p className="text-sm text-stone-600 leading-relaxed max-w-lg mx-auto mb-6">
          You started setting up payouts but didn't finish. Click below to continue where you left off.
        </p>
        <Button
          onClick={resumeOnboarding}
          disabled={redirecting}
          className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-6 h-11 gap-2"
          data-testid="stripe-connect-resume-btn"
        >
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          {redirecting ? "Redirecting…" : "Continue setup"}
        </Button>
      </div>
    );
  }

  // State 3: Onboarding complete but payouts not yet enabled (verification pending)
  if (status.onboarding_complete && !status.payouts_enabled) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 sm:p-10 text-center" data-testid="stripe-connect-state-3">
        <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
          <Shield className="w-7 h-7 text-blue-600" />
        </div>
        <h3 className="font-heading font-semibold text-xl text-stone-900 mb-3" data-testid="stripe-connect-heading-3">
          Verification in progress
        </h3>
        <p className="text-sm text-stone-600 leading-relaxed max-w-lg mx-auto">
          Stripe is reviewing your information. This usually takes a few minutes. You'll be notified when verification is complete.
        </p>
      </div>
    );
  }

  // State 4: Fully connected (payouts enabled)
  return (
    <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 sm:p-10" data-testid="stripe-connect-state-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-stone-900" data-testid="stripe-connect-heading-4">
              Connected to Stripe
            </h3>
            <p className="text-sm text-stone-500 mt-0.5">
              Your payouts are active. Earnings from completed bookings will be sent to your linked account.
            </p>
          </div>
        </div>
        <Button
          onClick={openDashboard}
          disabled={redirecting}
          variant="outline"
          className="rounded-full gap-2 shrink-0"
          data-testid="stripe-connect-dashboard-btn"
        >
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Manage payout details
        </Button>
      </div>
    </div>
  );
}


export default function MyProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "payments" ? "payments" : "profile";
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    location: "",
    bio: "",
  });

  const [profileStats, setProfileStats] = useState({ items: 0, bookings: 0, rating: 0, reviewCount: 0 });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        location: user.location || "",
        bio: user.bio || "",
      });
      // Fetch real stats
      Promise.all([
        api.get("/items", { params: { limit: 100 } }),
        api.get("/bookings"),
        api.get(`/users/${user.id}`).catch(() => null)
      ]).then(([itemsRes, bookingsRes, profileRes]) => {
        const myItems = itemsRes.data.items.filter(i => i.owner_id === user.id).length;
        const myBookings = bookingsRes.data.filter(b => b.borrower_id === user.id || b.lender_id === user.id).length;
        const avgRating = profileRes?.data?.avg_rating || 0;
        const reviewCount = profileRes?.data?.review_count || 0;
        setProfileStats({ items: myItems, bookings: myBookings, rating: avgRating, reviewCount });
      }).catch(console.error);
    }
  }, [user]);

  const loadEarnings = () => {
    if (earnings || loadingEarnings) return;
    setLoadingEarnings(true);
    api.get("/user/earnings")
      .then((res) => setEarnings(res.data))
      .catch(console.error)
      .finally(() => setLoadingEarnings(false));
  };

  // Auto-load earnings when Payments tab is selected via URL (e.g. return from Stripe)
  useEffect(() => {
    if (initialTab === "payments") loadEarnings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-stone-500 mb-4">Please log in to view your profile</p>
          <Link to="/auth">
            <Button className="rounded-full bg-coral hover:bg-coral text-white">
              Log In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put("/auth/profile", {
        name: formData.name,
        bio: formData.bio,
        location: formData.location,
      });
      if (refreshUser) await refreshUser();
      toast.success("Profile updated successfully!");
      setEditing(false);
    } catch (err) {
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingId(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post("/verification/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (refreshUser) await refreshUser();
      toast.success("Thank you for submitting your ID. A member of our team will verify your ID and will approve your verification soon.");
    } catch (err) {
      toast.error("Failed to upload ID. Please try again.");
    } finally {
      setUploadingId(false);
    }
  };

  const memberSince = user.created_at 
    ? new Date(user.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "2024";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="my-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-stone-500 hover:text-stone-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <h1 className="font-heading font-bold text-xl text-stone-900">My Profile</h1>
        <div className="w-16" />
      </div>

      {/* Profile Card Header */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 sm:p-8">
          <div className="flex items-start gap-5">
            <div className="relative">
              <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                <AvatarFallback className="bg-coral text-white text-3xl font-heading font-semibold">
                  {user.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {user.is_verified && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white" style={{display:'none'}}>
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="font-heading font-bold text-2xl text-stone-900">{user.name}</h2>
              </div>
              <p className="text-stone-500 text-sm mt-1">{user.email}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-stone-500">
                {user.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {user.location}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> Member since {memberSince}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs: Profile | Payments */}
      <Tabs defaultValue={initialTab} className="w-full" onValueChange={(v) => { if (v === "payments") loadEarnings(); }}>
        <TabsList className="mb-6 w-full justify-start">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-lg text-stone-900">Personal Info</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(!editing)}
                  className="rounded-full gap-2"
                  data-testid="edit-profile-btn"
                >
                  {editing ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                  {editing ? "Cancel" : "Edit"}
                </Button>
              </div>

              {editing ? (
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="h-12 pl-10 rounded-xl"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="h-12 pl-10 rounded-xl"
                        placeholder="City, State"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="bio">About Me</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="mt-1 rounded-xl min-h-[100px]"
                      placeholder="Tell others a bit about yourself..."
                    />
                  </div>
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white btn-press"
                    data-testid="save-profile-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Save className="w-4 h-4" /> Save Changes
                      </span>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {user.bio && (
                    <div>
                      <h3 className="text-sm font-medium text-stone-700 mb-2">About</h3>
                      <p className="text-stone-600 text-sm leading-relaxed">{user.bio}</p>
                    </div>
                  )}
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <p className="text-2xl font-heading font-bold text-stone-900">{profileStats.items}</p>
                      <p className="text-xs text-stone-500 mt-1">Items Listed</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <p className="text-2xl font-heading font-bold text-stone-900">{profileStats.bookings}</p>
                      <p className="text-xs text-stone-500 mt-1">Bookings</p>
                    </div>
                    <div className="text-center p-4 bg-stone-50 rounded-xl">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="w-5 h-5 text-orange-400 fill-orange-400" />
                        <span className="text-2xl font-heading font-bold text-stone-900">{profileStats.rating > 0 ? profileStats.rating : '-'}</span>
                      </div>
                      <p className="text-xs text-stone-500 mt-1">Rating</p>
                    </div>
                  </div>
                </div>
              )}

              {/* ID Verification Section — hidden, moved to V2 */}
            </div>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <div className="space-y-6">
            {/* Stripe Connect / Caribbean / Onboarding state */}
            <StripeConnectPanel />

            {/* Earnings Summary */}
            {earnings && (
              <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl p-6 text-white" data-testid="earnings-summary">
                <h3 className="font-heading font-semibold text-lg mb-4">Earnings Overview</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-white/60 mb-1">Available</p>
                    <p className="font-heading font-bold text-xl">${earnings.available_balance?.toFixed(2)}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-white/60 mb-1">Pending</p>
                    <p className="font-heading font-bold text-xl text-amber-300">${earnings.pending_earnings?.toFixed(2)}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-white/60 mb-1">Total Earned</p>
                    <p className="font-heading font-bold text-xl text-emerald-300">${earnings.total_earnings?.toFixed(2)}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3">
                    <p className="text-xs text-white/60 mb-1">Paid Out</p>
                    <p className="font-heading font-bold text-xl">${earnings.total_paid_out?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payout History */}
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="payout-history">
              <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-stone-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-stone-900">Payout History</h4>
                    <p className="text-xs text-stone-500">Your completed and pending earnings</p>
                  </div>
                </div>
              </div>

              {loadingEarnings ? (
                <div className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-stone-300 border-t-coral rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-stone-400">Loading history...</p>
                </div>
              ) : !earnings || (earnings.completed_bookings?.length === 0 && earnings.active_bookings?.length === 0) ? (
                <div className="p-8 text-center">
                  <TrendingUp className="w-10 h-10 mx-auto text-stone-200 mb-3" />
                  <p className="text-stone-500 text-sm">No earnings yet</p>
                  <p className="text-stone-400 text-xs mt-1">Earnings will appear here once borrowers pay for their bookings</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {/* Active / Pending Earnings */}
                  {earnings.active_bookings?.map((b) => (
                    <div key={b.id} className="p-4 flex items-center gap-3" data-testid={`earning-active-${b.id}`}>
                      <img
                        src={getImageUrl(b.item?.images?.[0]) || "https://via.placeholder.com/40"}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-stone-900 truncate">{b.item?.title || "Booking"}</p>
                        <p className="text-xs text-stone-500">{b.start_date} — {b.end_date}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-sm text-amber-600">+${b.total_price?.toFixed(2)}</p>
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                          {b.status === "confirmed" ? "Confirmed" : "Active"}
                        </span>
                      </div>
                    </div>
                  ))}
                  {/* Completed Earnings */}
                  {earnings.completed_bookings?.map((b) => (
                    <div key={b.id} className="p-4 flex items-center gap-3" data-testid={`earning-completed-${b.id}`}>
                      <img
                        src={getImageUrl(b.item?.images?.[0]) || "https://via.placeholder.com/40"}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-stone-900 truncate">{b.item?.title || "Booking"}</p>
                        <p className="text-xs text-stone-500">{b.start_date} — {b.end_date}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-sm text-emerald-600">+${b.total_price?.toFixed(2)}</p>
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                          Completed
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Quick Links */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <Link 
          to="/dashboard"
          className="p-4 bg-white rounded-xl border border-stone-200 hover:border-coral hover:shadow-md transition-all text-center"
        >
          <p className="font-medium text-stone-900">Dashboard</p>
          <p className="text-xs text-stone-500 mt-1">Manage listings & bookings</p>
        </Link>
        <Link 
          to="/create-listing"
          className="p-4 bg-white rounded-xl border border-stone-200 hover:border-coral hover:shadow-md transition-all text-center"
        >
          <p className="font-medium text-stone-900">List an Item</p>
          <p className="text-xs text-stone-500 mt-1">Start earning today</p>
        </Link>
      </div>
    </div>
  );
}
